import { useEffect, useState } from "react"
import { ChartMedianPrice } from "../../presentationals/ChartMedianPrice"
import { DateTime } from "luxon"
import { ReadMonitorRunResultParams, useReadMonitorRunResultGroupedByMonitorIdQuery } from "services/monitorRunResult"
import { compareLuxonDates } from "shared/lib/compareLuxonDates"
import { ChartPaginationModel } from "../../presentationals/ChartPagination/ChartPagination"
import { Box, SxProps } from "@mui/material"
import { ChartDatePanel } from "../../presentationals/ChartDatePanel"
import { ButtonStyle, ChartDateMode } from "../../presentationals/ChartDatePanel/ChartDatePanel"
import { DateRangePickerDialog } from "../../presentationals/DateRangePickerDialog"
import { median } from "shared/lib/median"
import { useReadMonitorByIdQuery } from "services/monitor"
import { LimitedDataForPeriodDialog } from "../../presentationals/LimitedDataForPeriodDialog"
import { DateTimePeriod } from 'shared/types/DateTimePeriod'

interface ChartMedianPriceContainerProps {
    monitorId?: number
}

export const ChartMedianPriceContainer = (props: ChartMedianPriceContainerProps): JSX.Element => {
    const {
        monitorId
    } = props
    const [userPeriodManually, setUserPeriodManually] = useState<boolean>(false)
    const [periodContainingData, setPeriodContainingData] = useState<DateTimePeriod | undefined>(undefined)
    const [openLimitedDataForPeriodDialog, setOpenLimitedDataForPeriodDialog] = useState<boolean>(false)
    const [readMonitorRunResultByMonitorIdQueryParams, setReadMonitorRunResultByMonitorIdQueryParams] = useState<ReadMonitorRunResultParams>({
        monitorId: undefined,
    })

    const [readMonitorRunResultByMonitorIdQueryOptions, setReadMonitorRunResultByMonitorIdQueryOptions] = useState<any>({
        skip: monitorId === undefined,
        refetchOnMountOrArgChange: true,
        pollingInterval: 5000,
    })

    useEffect(() => {
        setReadMonitorRunResultByMonitorIdQueryParams(
            {
                monitorId: monitorId,
            }
        )

        setReadMonitorRunResultByMonitorIdQueryOptions({
            skip: monitorId === undefined,
            refetchOnMountOrArgChange: true,
            pollingInterval: 5000,
        })

    }, [
        monitorId
    ])

    useEffect(() => {
        setReadMonitorByIdQueryParams((prevValue: any) => {
            return {
                ...prevValue,
                monitorId: monitorId
            }
        });

        setReadMonitorByIdOptions((prevValue: any) => {
            return {
                    ...prevValue,
                    skip: monitorId === undefined,
                }
        })
    }, [
        monitorId
    ])

    const {
        data: readMonitorRunResultGroupedByMonitorIdData,
        isLoading: readMonitorRunResultGroupedByMonitorIdsLoading,
        isFetching: readMonitorRunResultGroupedByMonitorIdIsFetching,
    } = useReadMonitorRunResultGroupedByMonitorIdQuery(
        readMonitorRunResultByMonitorIdQueryParams,
        readMonitorRunResultByMonitorIdQueryOptions
        );

    const [readMonitorByIdQueryParams, setReadMonitorByIdQueryParams] = useState<any>(undefined)
    const [readMonitorByIdOptions, setReadMonitorByIdOptions] = useState<any>(
        {
            skip: true
        }
    );

    const {
        data: readMonitorByIdQueryData,
        isLoading: readMonitorByIdQueryIsLoading
    } = useReadMonitorByIdQuery(readMonitorByIdQueryParams, readMonitorByIdOptions)

    const [chartMedianPriceData, setChartMedianPriceData] = useState<Object | undefined>()
    const [toMedianPriceGrow, setToMedianPriceGrow] = useState<number>()
    const [toMedianPriceGrowPercent, setToMedianPriceGrowPercent] = useState<number>()
    const [data, setData] = useState<any>()
    const [chartDateMode, setChartDateMode] = useState<ChartDateMode | undefined>(undefined)
    const [medianMarketDirection, setMedianMarketDirection] = useState<number | undefined>(undefined)
    const [medianMarketDirectionGrow, setMedianMarketDirectionGrow] = useState<number | undefined>(undefined)
    const [medianMarketDirectionGrowPercent, setMedianMarketDirectionGrowPercent] = useState<number | undefined>(undefined)
    const [chartDateTimePeriod, setChartDateTimePeriod] = useState<DateTimePeriod | undefined>(undefined)

    const [chartMedianPriceOptions, setChartMedianPriceOptions] = useState<any>({
        elements: {
            point: {
                radius: 5,
                hoverRadius: 7, 
                hitRadius: 16
            }
        },
        responsive: true,
        scales: {
            y: {

                ticks: {
                    color: "#000000",
                    precision: 0,
                    beginAtZero: true
                }
            },
            x: {
                type: "timeseries",
                ticks: {
                    color: "#000000",
                    maxTicksLimit: 6
                },
                time: {
                    displayFormats: {
                        day: 'MM/dd/yyyy',
                        hour: 'MM/dd/yyyy',
                        minute: 'MM/dd/yyyy',
                    },
                },
                adapters: {
                    date: {
                        zone: 'utc'
                    }
                },

            }
        },
        plugins: {
            title: {
                display: true,
                text: 'Median Price',
                font: {
                    size: 16
                }
            },
            legend: false,
            annotation: {
                annotations: {
                    label1: {
                        display: (obj: any, args: any, options: any) => {
                            return !obj.chart.data?.datasets.some((dataset: any) => dataset.data.length > 0)
                        },
                        type: 'label',
                        position: 'center',
                        backgroundColor: 'rgba(0,0,0, 0)',
                        content: ['No data available'],
                        font: {
                            size: 36,
                            weight: 600
                        }
                    }
                }
            }
        }
    })

    useEffect(() => {
        if (readMonitorByIdQueryData === undefined) {
            return
        }

        if (readMonitorByIdQueryData.customPeriods && readMonitorByIdQueryData.customPeriods.length > 0) {
            const startDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].startDateTimeUtc, { zone: "UTC" }).startOf('day')
            const endDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].endDateTimeUtc, { zone: "UTC" }).endOf('day')
            

            setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
                setChartDateTimePeriod({
                    from: startDate,
                    to: endDate
                })
            })

            setChartDateMode('Custom')

            return
        }

        setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
            setChartDateTimePeriod({
                from: DateTime.now().startOf('day'),
                to: DateTime.now().endOf('day').plus({ month: 1 })
            })
        })

        setChartDateMode('1M')
    },
        [
            readMonitorByIdQueryData
        ]
    )

    useEffect(() => {
        if (chartDateTimePeriod === undefined) {
            setData(undefined)

            return
        }


        if (readMonitorRunResultGroupedByMonitorIdData === undefined) {
            setData(undefined)

            return
        }

        if (readMonitorByIdQueryData === undefined) {
            setData(undefined)

            return
        }

        const transformedData: any[] = readMonitorRunResultGroupedByMonitorIdData.list
            .map((resultItem: any, index: number, list: any) => {
                const checkinUtc = DateTime.fromISO(resultItem?.checkinUtc, { setZone: true });

                return {
                    checkinUtc: checkinUtc,
                    medianPrice: resultItem.medianPrice
                }
            })
            .sort(compareLuxonDates)

        let trData = transformedData;


        if (chartDateTimePeriod !== undefined) {
            const fromDt = DateTime.fromObject({
                year: chartDateTimePeriod.from.year,
                month: chartDateTimePeriod.from.month,
                day: chartDateTimePeriod.from.day
            }, { zone: "UTC" })

            const toDt = DateTime.fromObject({
                year: chartDateTimePeriod.to.year,
                month: chartDateTimePeriod.to.month,
                day: chartDateTimePeriod.to.day,
                hour: 23,
                minute: 59,
                second: 59,
                millisecond: 999

            }, { zone: "UTC" })

            trData = transformedData.filter((row: any) => {
                return row.checkinUtc >= fromDt && row.checkinUtc <= toDt
            })
        }

        setData(trData)


        if (trData !== undefined && trData.length > 0) {
            const minCheckInUtcValue = trData[0].checkinUtc
            const maxCheckInUtcValue = trData[trData.length - 1].checkinUtc

            const diffMinInDays = minCheckInUtcValue.diff(chartDateTimePeriod?.from, 'days').days;
            const diffMaxInDays = maxCheckInUtcValue.diff(chartDateTimePeriod?.to, 'days').days;


            if (!userPeriodManually) {
                return
            }

            if (diffMinInDays < - 1 || diffMaxInDays < -1) {
                setPeriodContainingData({
                    from: minCheckInUtcValue,
                    to: maxCheckInUtcValue
                })
                setOpenLimitedDataForPeriodDialog(true)
            }
        }
    }, [
        readMonitorRunResultGroupedByMonitorIdData,
        chartDateTimePeriod,
        readMonitorByIdQueryData
    ])

    useEffect(() => {
        function cleanState() {
            setToMedianPriceGrow(undefined)
            setToMedianPriceGrowPercent(undefined)
            setMedianMarketDirection(undefined)
            setMedianMarketDirectionGrow(undefined)
            setMedianMarketDirectionGrowPercent(undefined)
            setChartMedianPriceData({
                datasets: [],
            })
        }

        function handleChartMedianPriceOptions() {
            setChartMedianPriceOptions((prevValue: any) => {
                const scales = {
                    ...prevValue.scales
                }

                let maxValue = Math.max.apply(null, data.map((o: any) => { return o.medianPrice; }));
                let minValue = Math.min.apply(null, data.map((o: any) => { return o.medianPrice; }));

                if (readMonitorByIdQueryData.priceForComparison !== undefined
                    && readMonitorByIdQueryData.priceForComparison !== null) {
                    if (readMonitorByIdQueryData.priceForComparison < minValue) {
                        minValue = readMonitorByIdQueryData.priceForComparison
                    }

                    if (readMonitorByIdQueryData.priceForComparison > maxValue) {
                        maxValue = readMonitorByIdQueryData.priceForComparison
                    }
                }

                scales.y.min = minValue - 10;
                scales.y.max = maxValue + 10;

                return ({
                    ...prevValue,
                    scales
                });
            })
        }

        function handleStateAndDatasets() {
            if (data === undefined || data.length <= 0) {
                return
            }

            const datasets: any = []
            const startItem: any = data.find((row: any) => row.medianPrice !== null)
            const endItem = data.findLast((row: any) => row.checkinUtc !== null)

            if (startItem === undefined || endItem === undefined) {
                return
            }

            const startMedianPrice = startItem.medianPrice
            const endMedianPrice = endItem.medianPrice
            const medianPriceGrow = endMedianPrice - startMedianPrice
            let percentGrow = undefined

            if (startMedianPrice === 0) {
                percentGrow = (medianPriceGrow / 1) * 100
            } else {
                percentGrow = (medianPriceGrow / startMedianPrice) * 100
            }

            if (medianPriceGrow >= 0) {
                percentGrow = Math.abs(percentGrow)
            }

            setToMedianPriceGrow(medianPriceGrow)
            setToMedianPriceGrowPercent(percentGrow)


            let diffMedianStartMedianSumm = undefined
            const medianResult = median(data.map((o: any) => { return (o.medianPrice) }))
            if (medianResult !== undefined) {
                diffMedianStartMedianSumm = medianResult - startMedianPrice
                let percentMedianMarketGrow: number | undefined = undefined

                if (startMedianPrice === 0) {
                    percentMedianMarketGrow = (diffMedianStartMedianSumm / 1) * 100
                } else {
                    percentMedianMarketGrow = (diffMedianStartMedianSumm / startMedianPrice) * 100
                }

                if (diffMedianStartMedianSumm >= 0) {
                    percentMedianMarketGrow = Math.abs(percentMedianMarketGrow)
                }

                setMedianMarketDirection(medianResult)
                setMedianMarketDirectionGrow(diffMedianStartMedianSumm)
                setMedianMarketDirectionGrowPercent(percentMedianMarketGrow)
                const borderColor = diffMedianStartMedianSumm < 0 ? '#FE0000' : '#4caf50'

                datasets.push(
                    {
                        fill: 'start',
                        type: 'line' as const,
                        label: 'Market direction',
                        data: [
                            {
                                medianMarketDirectionGrow: startItem.medianPrice,
                                checkinUtc: startItem.checkinUtc
                            },
                            {
                                medianMarketDirectionGrow: medianResult,
                                checkinUtc: endItem.checkinUtc
                            }
                        ],
                        parsing: {
                            yAxisKey: 'medianMarketDirectionGrow',
                            xAxisKey: 'checkinUtc',
                        },
                        borderColor: borderColor,
                        backgroundColor: () => (`rgba(254, 0, 0, 0)`)
                    }
                )
            }

            const negativeDirection = diffMedianStartMedianSumm !== undefined && diffMedianStartMedianSumm < 0
            const mainGrapBorderColor = negativeDirection ? '#FE0000' : '#4caf50'

            datasets.push({
                fill: 'start',
                type: 'line' as const,
                label: 'Median Price',
                data: data,
                parsing: {
                    yAxisKey: 'medianPrice',
                    xAxisKey: 'checkinUtc',
                },
                borderColor: mainGrapBorderColor,
                backgroundColor: function (context: any): any {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        return;
                    }

                    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                    if (negativeDirection) {
                        gradient.addColorStop(0, 'rgba(254, 0, 0, 0.2)');
                        gradient.addColorStop(1, 'rgba(254, 0, 0, 0.05)');
                    } else {
                        gradient.addColorStop(0, 'rgba(76, 176, 80, 0.2)');
                        gradient.addColorStop(1, 'rgba(76, 176, 80, 0.05)');
                    }

                    return gradient;
                },
            })


            datasets.push({
                    fill: 'start',
                    type: 'line' as const,
                    label: 'Your price',
                    data: [
                        {
                            priceForComparison: readMonitorByIdQueryData.priceForComparison,
                            checkinUtc: startItem.checkinUtc
                        },
                        {
                            priceForComparison: readMonitorByIdQueryData.priceForComparison,
                            checkinUtc: endItem.checkinUtc
                        }
                    ],
                    parsing: {
                        yAxisKey: 'priceForComparison',
                        xAxisKey: 'checkinUtc',
                    },
                borderColor: `#FE0000`,
                    backgroundColor: () => (`rgba(254, 0, 0, 0)`)
            })

            setChartMedianPriceData({
                datasets: datasets,
            })
        }

        cleanState()

        if (data === undefined) {
            return
        }

        handleChartMedianPriceOptions()
        handleStateAndDatasets()
    }, [
        data
    ])

    const chartDatePanelButtonStyle: ButtonStyle = {
        color: {
            default: '#C0C0C0',
            hover: '#FE0000',
            selected: '#FE0000'
        },
        borderColor: {
            default: '#C0C0C0',
            hover: '#FE0000',
            selected: '#FE0000'
        }
    }

    const handleModeChange = (mode: ChartDateMode) => {
        setChartDateMode(mode)
        setUserPeriodManually(true)
    }

    const handleDateTimePeriodChange = (period: DateTimePeriod) => {
        setChartDateTimePeriod(period)
        setUserPeriodManually(true)
    }

    const namePropStyle = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0 0 10px 0',
    }

    const propertyListContainer = {
        padding: '20px 0px',
        gridTemplateColumns: 'max-content max-content max-content',
        display: 'grid',

    }

    const valuePropStyle: SxProps = {
        margin: '0 0 10px 20px',
        fontSize: '20px',
        '&.CellValue__Root_isGrowing': {
            color: `#4CB050`
        },
        '&.CellValue__Root_isFalling': {
            color: `#FE0000`
        }
    }


    const renderValue = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(2)}`

        if (value < 0) {
            //classes = 'CellValue__Root_isFalling'
            handledValue = `-$${Math.abs(value).toFixed(2)}`
        } else if (value > 0) {
            //classes = 'CellValue__Root_isGrowing'
            handledValue = `+$${Math.abs(value).toFixed(2)}`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const renderValuePercent = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged` 
        let handledValue = `${value.toFixed(2)}%`

        if (value < 0) {
            classes = 'CellValue__Root_isFalling'
            handledValue = `${value.toFixed(2)}%`
        } else if (value > 0) {
            classes = 'CellValue__Root_isGrowing'
            handledValue = `+${value.toFixed(2)}%`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const datesContainer: SxProps = {
        padding: '0 0',
        gridTemplateColumns: 'max-content 1fr',
        display: 'grid',
        fontSize: '20px'
    }

    const propertyTitle: SxProps = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0'
    }

    const toDateFormat = (date: DateTime) => {
        return date.toFormat('M/dd/yyyy')
    }

    const handleLimitedDataForPeriodDialogClose = () => {
        setOpenLimitedDataForPeriodDialog(false)
    }

    return (
        <>
            <Box>
                <Box sx={datesContainer}>
                    <Box
                        sx={propertyTitle}
                    >
                        Start:
                    </Box>
                    <Box>
                        {chartDateTimePeriod?.from ? toDateFormat(chartDateTimePeriod.from) : ''}
                    </Box>
                    <Box
                        sx={propertyTitle}
                    >
                        End:
                    </Box>
                    <Box>
                        {chartDateTimePeriod?.to ? toDateFormat(chartDateTimePeriod.to) : ''}
                    </Box>
                </Box>
                <ChartMedianPrice
                    data={chartMedianPriceData}
                    options={chartMedianPriceOptions}
                    loading={chartMedianPriceData === undefined}
                />
                <ChartDatePanel
                    buttonStyle={chartDatePanelButtonStyle}
                    mode={chartDateMode}
                    onModeChange={handleModeChange}
                    onDateTimePeriodChange={handleDateTimePeriodChange}
                    dateTimePeriod={chartDateTimePeriod}
                />
                <Box sx={propertyListContainer}>
                    <Box sx={namePropStyle}>
                        Median price:
                    </Box>
                    {
                        renderValue(toMedianPriceGrow)
                    }
                    {
                        renderValuePercent(toMedianPriceGrowPercent)
                    }
                    <Box sx={namePropStyle}>
                         Market direction:
                    </Box>
                    {
                        renderValue(medianMarketDirectionGrow)
                    }
                    {
                        renderValuePercent(medianMarketDirectionGrowPercent)
                    }
                </Box>

                <LimitedDataForPeriodDialog
                    open={openLimitedDataForPeriodDialog}
                    onClose={handleLimitedDataForPeriodDialogClose}
                    selectedPeriod={chartDateTimePeriod}
                    periodContainingData={periodContainingData}
                />
            </Box>
        </>       
    )
}